import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Mail, 
  DollarSign, 
  CheckCircle, 
  Users, 
  Shield, 
  Zap,
  ArrowRight,
  MousePointerClick,
  ClipboardList,
  Smartphone,
  MessageSquare,
  Search,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Gift,
  Wallet
} from "lucide-react";
import { useState } from "react";

import Footer from "../components/Footer";

export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "What is GigsEasy?",
      answer: "GigsEasy.com is an online rewards community that allows you to monetize your time and efforts. You can earn cash by reading emails, participating in surveys, completing offers, referring friends, and more!"
    },
    {
      question: "How will I be paid?",
      answer: "Participants will be paid weekly. To receive payment, member commissions must exceed $25; otherwise, commissions earned in a month below $25 will be carried over to the following month. Participants can review their payment history and payment statuses at any time in their profile."
    },
    {
      question: "Is there any cost to open an account with GigsEasy?",
      answer: "Joining is entirely free. We compensate you for your time and hard work!"
    },
    {
      question: "Does GigsEasy accept international participants?",
      answer: "Certainly! Our program is accessible worldwide, but payments will exclusively be issued in US currency."
    },
    {
      question: "Can I sign up more than once?",
      answer: "No, each member is permitted to register only once. Please be aware that only one member from the same household is permitted to sign up. Members who attempt multiple registrations with identical or similar names or addresses will not receive payment, and their accounts will be terminated."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-2">
              <img 
                src="/images/gigseasy-logo.svg" 
                alt="GigsEasy Logo" 
                className="h-10"
              />
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="nav-login-btn" className="text-slate-600 hover:text-primary font-medium">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button 
                  data-testid="nav-register-btn" 
                  className="bg-gradient-to-r from-[#00B894] to-[#0984E3] text-white hover:opacity-90 rounded-full px-6 font-medium shadow-lg shadow-[#00B894]/25 transition-all hover:-translate-y-0.5"
                >
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 lg:px-12 bg-gradient-to-br from-white via-[#00B894]/5 to-[#0984E3]/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Get Paid By Reading Emails And Performing{" "}
                <span className="bg-gradient-to-r from-[#00B894] to-[#0984E3] bg-clip-text text-transparent">
                  Simple Tasks.
                </span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                Earn cash daily by reading emails and participating in survey opportunities. 
                Engage with our platform to maximize your earnings while sharing your opinions and feedback on various topics.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button 
                    data-testid="hero-get-started-btn"
                    className="w-full sm:w-auto bg-gradient-to-r from-[#00B894] to-[#0984E3] text-white px-8 py-6 text-lg font-medium rounded-full shadow-lg shadow-[#00B894]/25 hover:opacity-90 hover:-translate-y-0.5 transition-all"
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button 
                    variant="outline"
                    data-testid="hero-login-btn"
                    className="w-full sm:w-auto px-8 py-6 text-lg font-medium rounded-full border-2 border-slate-200 hover:border-[#00B894] hover:text-[#00B894] transition-all"
                  >
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/images/hero-image.png" 
                alt="GigsEasy Platform"
                className="w-full h-auto drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How To Start Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="/images/tasks-image.png" 
                alt="GigsEasy Tasks"
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Super Easy And Quick To Start With{" "}
                <span className="bg-gradient-to-r from-[#00B894] to-[#0984E3] bg-clip-text text-transparent">
                  Free Account
                </span>
              </h2>
              <p className="text-slate-600 mb-10">
                Establish your free account in under a minute and start making money.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    step: "1",
                    title: "Sign Up With Your Contact Information",
                    desc: "Create your profile and confirm email address."
                  },
                  {
                    step: "2",
                    title: "Add Your Payment Details",
                    desc: "We have multiple payment methods depend."
                  },
                  {
                    step: "3",
                    title: "Start Making Money",
                    desc: "Start receiving emails and work on other simple tasks."
                  }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#00B894] to-[#0984E3] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-slate-900">{item.title}</h3>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6 lg:px-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Multiple ways to earn money from the comfort of your home
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Mail,
                title: "Get paid to read emails",
                description: "Get paid to read emails, explore new deals.",
                gradient: "from-[#00B894] to-[#00D9A5]"
              },
              {
                icon: ClipboardList,
                title: "Get paid to answer surveys",
                description: "Select surveys you are willing to participate and get paid.",
                gradient: "from-[#0984E3] to-[#00B4FF]"
              },
              {
                icon: Smartphone,
                title: "Get paid to test the apps",
                description: "Test mobile applications and play games and get paid.",
                gradient: "from-[#6C5CE7] to-[#A29BFE]"
              },
              {
                icon: MessageSquare,
                title: "Get paid to moderate social media",
                description: "Moderate social media communities, pages, groups, and get paid.",
                gradient: "from-[#FD79A8] to-[#FDCB6E]"
              },
              {
                icon: CheckCircle,
                title: "Get paid completing simple tasks",
                description: "Perform simple tasks from the comfort of your house and get paid.",
                gradient: "from-[#00B894] to-[#0984E3]"
              },
              {
                icon: Search,
                title: "Get paid to search internet",
                description: "Search internet for specific keywords or information.",
                gradient: "from-[#E17055] to-[#FDCB6E]"
              }
            ].map((task, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-slate-100"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${task.gradient} flex items-center justify-center mb-6`}>
                  <task.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">{task.title}</h3>
                <p className="text-slate-600 mb-4">{task.description}</p>
                <Link to="/register" className="text-[#00B894] font-medium hover:text-[#0984E3] transition-colors inline-flex items-center gap-1">
                  Sign up <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/register">
              <Button 
                className="bg-gradient-to-r from-[#00B894] to-[#0984E3] text-white px-10 py-6 text-lg font-medium rounded-full shadow-lg shadow-[#00B894]/25 hover:opacity-90 hover:-translate-y-0.5 transition-all"
              >
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                GigsEasy Makes It Simple To Make Money Online.{" "}
                <span className="bg-gradient-to-r from-[#00B894] to-[#0984E3] bg-clip-text text-transparent">
                  Get Paid Weekly
                </span>{" "}
                With Multiple Payment Options.
              </h2>
              <p className="text-slate-600 mb-8">
                We work with multiple payment methods, including PayPal Cash, Amazon Gift Cards, Branded Gift Cards, Payoneer and others.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                {[
                  { icon: Wallet, name: "PayPal" },
                  { icon: Gift, name: "Amazon" },
                  { icon: CreditCard, name: "Payoneer" },
                  { icon: Gift, name: "Gift Cards" }
                ].map((payment, index) => (
                  <div key={index} className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-full">
                    <payment.icon className="w-5 h-5 text-[#00B894]" />
                    <span className="text-slate-700 font-medium">{payment.name}</span>
                  </div>
                ))}
              </div>

              <Link to="/register">
                <Button 
                  className="bg-gradient-to-r from-[#00B894] to-[#0984E3] text-white px-8 py-6 text-lg font-medium rounded-full shadow-lg shadow-[#00B894]/25 hover:opacity-90 hover:-translate-y-0.5 transition-all"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div>
              <img 
                src="/images/payment-methods.png" 
                alt="Payment Methods"
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 lg:px-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions?
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
                  data-testid={`faq-${index}`}
                >
                  <h3 className="font-heading font-semibold text-lg text-slate-900 pr-4">
                    {faq.question}
                  </h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#00B894] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-slate-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-gradient-to-r from-[#00B894] to-[#0984E3]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Start Earning?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of members who are already earning by completing simple tasks. 
            Sign up today and get your first task within minutes.
          </p>
          <Link to="/register">
            <Button 
              data-testid="cta-register-btn"
              className="bg-white text-[#00B894] px-10 py-6 text-lg font-medium rounded-full shadow-lg hover:bg-slate-50 hover:-translate-y-0.5 transition-all"
            >
              Create Free Account
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
